import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BoltIcon from '@mui/icons-material/Bolt';
import { StandardButton, Txt } from '../design-system';

interface TrialPaywallProps {
  firstName?: string;
  onStartTrial: () => void;
  onSkip: () => void;
}

const VALUE_POINTS = [
  'Photograph every meal, no daily cap',
  'Bloodwork analysis across 80+ biomarkers',
  'Supplement checks: form, timing and how your bloodwork responds',
  'Ask the coach as much as you like',
  'Every FuelTron score, in full',
];

/**
 * Prominent-but-skippable 7-day free trial screen, shown once, the first time the
 * app is opened after the free Pro day ends. Primary button goes to the platform
 * purchase flow; a quieter link stays on the free version.
 */
const TrialPaywall: React.FC<TrialPaywallProps> = ({ firstName, onStartTrial, onSkip }) => {
  return (
    <Box
      sx={{
        position: 'fixed', inset: 0, zIndex: 1400,
        background: 'linear-gradient(160deg, #0a0e14 0%, #101428 100%)',
        overflowY: 'auto',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        px: 3, py: 4,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 460, m: 'auto', display: 'flex', flexDirection: 'column' }}>
        {/* Badge */}
        <Box className="icon-text-row" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
          <BoltIcon sx={{ color: '#FFD740', filter: 'drop-shadow(0 0 10px #FFD740)' }} />
          <Typography sx={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '0.7rem', letterSpacing: 3, color: '#FFD740', textTransform: 'uppercase' }}>
            FuelTron Pro
          </Typography>
        </Box>

        {/* Headline */}
        {/* Txt, not Typography: global.css forces body Typography to a small fixed size on phones,
            which shrank this headline to body text (2026-10-12). */}
        <Txt
          sx={{
            fontWeight: 800, textAlign: 'center', letterSpacing: '-0.5px',
            fontSize: { xs: '1.9rem', sm: '2.2rem' }, lineHeight: 1.15, mb: 1.5,
            background: 'linear-gradient(90deg, #00E0FF, #00E676)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}
        >
          {firstName && firstName.toLowerCase() !== 'there' && /^[A-Za-z][A-Za-z'’-]+$/.test(firstName) ? `${firstName}, your Pro day is over.` : 'Your Pro day is over.'}
        </Txt>
        <Txt sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.75)', fontSize: '1rem', lineHeight: 1.5, mb: 3 }}>
          Keep photo logging and everything else you used yesterday with a <b style={{ color: '#fff' }}>free week</b>. No charge today.
        </Txt>

        {/* Value list */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 3 }}>
          {VALUE_POINTS.map((pt, i) => (
            <Box key={i} className="icon-text-row" sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <CheckCircleIcon sx={{ color: '#00E676', fontSize: 20, flexShrink: 0, filter: 'drop-shadow(0 0 5px rgba(0,230,118,0.5))' }} />
              <Txt sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', lineHeight: 1.4 }}>{pt}</Txt>
            </Box>
          ))}
        </Box>

        {/* Primary CTA */}
        <StandardButton variant="gradient" onClick={onStartTrial} fullWidth size="large">
          Start my free week
        </StandardButton>

        {/* Disclosure */}
        <Typography sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', mt: 1.25, lineHeight: 1.5 }}>
          7 days free, then $7.99/month or $69.99/year (save 27%). Cancel any time. If you cancel before the trial ends, you pay nothing.
        </Typography>

        {/* Skippable secondary */}
        <Typography
          onClick={onSkip}
          sx={{
            textAlign: 'center', mt: 2.5, color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem',
            fontFamily: 'var(--font-mono)', letterSpacing: 0.5, cursor: 'pointer',
            textDecoration: 'underline', '&:hover': { color: 'rgba(255,255,255,0.8)' },
          }}
        >
          Continue with the free version
        </Typography>
      </Box>
    </Box>
  );
};

export default TrialPaywall;
